/**
 * Permanent right-side "current game" leaderboard.
 *
 * Always-on panel that sits to the right of the board and stays up through
 * every gameplay scene (board, poll_category, poll_clue_value, clue_zoom,
 * answer_reveal, intermission). Replaces the corner mini-LB — viewers now
 * always see the live standings without it floating over the board.
 *
 * Data source: `score_update.leaderboard_top10` / snapshot.leaderboard_top10
 * (current game only — resets between games, populated as players score).
 *
 * Layout:
 *   - 380px wide, absolutely positioned inside the 1920x1080 stage.
 *   - Vertically aligned with the board (top+bottom match the board's
 *     visible envelope). Board sits left-of-center at ~(-180, -80) world
 *     coords (see main.ts applyCameraLayout); this panel fills the right
 *     column at x=[1500..1880].
 *
 * Scrolling:
 *   - Shows ALL scored players (up to `score_update.leaderboard_top10.length`
 *     ceiling, which is 10 from the backend but the UI supports more). When
 *     the list overflows vertically, a CSS ping-pong animation scrolls the
 *     tail portion — reuses the same pattern the lobby standings panel uses
 *     (see lobby-leaderboard.ts).
 *
 * Visual language:
 *   - Reuses `.sg-lblb*` classes from lobby-leaderboard.ts so the rounded
 *     card, Oswald header, and gold/silver/bronze rank accents match the
 *     lobby top-10 panel exactly.
 *
 * @packageDocumentation
 */

import type { LbRow } from '@sg/shared';
import { fitText } from './fit-text.js';
import { ensureLobbyLbStyles } from './lobby-leaderboard.js';

export interface SideLeaderboardHandle {
  el: HTMLDivElement;
  setRows: (rows: LbRow[]) => void;
  setVisible: (v: boolean) => void;
  dispose: () => void;
}

// Variant-specific styles layered on top of the shared `.sg-lblb*` base.
// Only overrides differ: absolute positioning, fixed width/height, compact
// 3-col grid (rank | name | points — games column dropped), ping-pong scroll
// of the overflow list.
const VARIANT_STYLES = `
  @keyframes sg-sidelb-in {
    from { opacity: 0; transform: translateX(12px); }
    to   { opacity: 1; transform: translateX(0);    }
  }
  .sg-lblb[data-role="game-side-lb"] {
    position: absolute;
    top: 120px;
    left: 1500px;
    width: 380px;
    height: 840px;
    z-index: 50;
    pointer-events: none;
    animation: sg-sidelb-in 280ms ease-out;
  }
  .sg-lblb[data-role="game-side-lb"].hidden { display: none; }
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-title {
    font-size: 1.35rem;
    letter-spacing: 0.24em;
    padding: 10px 8px 8px 8px;
  }
  /* 3-column grid — rank | name | points (games column dropped for space). */
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-head,
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-row {
    grid-template-columns: 40px 1fr 86px;
  }
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-head {
    font-size: 0.72rem;
    padding: 6px 12px 4px 12px;
  }
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-row {
    padding: 8px 12px;
    font-size: 1.0rem;
  }
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-rank {
    font-size: 1.1rem;
  }
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-points {
    font-size: 1.05rem;
  }
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-empty {
    padding: 20px 14px;
    font-size: 0.95rem;
    line-height: 1.4;
  }
`;

function ensureVariantStyles(): void {
  if (document.getElementById('sg-sidelb-variant-style')) return;
  const s = document.createElement('style');
  s.id = 'sg-sidelb-variant-style';
  s.textContent = VARIANT_STYLES;
  document.head.appendChild(s);
}

function medalFor(rank: number): string {
  if (rank === 1) return 'sg-r1';
  if (rank === 2) return 'sg-r2';
  if (rank === 3) return 'sg-r3';
  return '';
}

interface RowState {
  playerId: number;
  displayedScore: number;
  targetScore: number;
  animFrom: number;
  animStartMs: number | null;
  rowEl: HTMLDivElement;
  nameEl: HTMLDivElement;
  scoreEl: HTMLDivElement;
  rankEl: HTMLDivElement;
}

export function createSideLeaderboard(mount: HTMLElement): SideLeaderboardHandle {
  ensureLobbyLbStyles();
  ensureVariantStyles();

  const el = document.createElement('div');
  el.className = 'sg-lblb';
  el.setAttribute('data-role', 'game-side-lb');
  el.innerHTML = `
    <div class="sg-lblb-title">LEADERBOARD</div>
    <div class="sg-lblb-head"><div>#</div><div>PLAYER</div><div>POINTS</div></div>
    <div class="sg-lblb-body">
      <div class="sg-lblb-scroll" data-role="scroll">
        <div class="sg-lblb-list" data-role="rows">
          <div class="sg-lblb-empty">No scores yet — be the first to answer!</div>
        </div>
      </div>
    </div>
  `;
  mount.appendChild(el);
  const body = el.querySelector<HTMLDivElement>('.sg-lblb-body')!;
  const scroll = el.querySelector<HTMLDivElement>('[data-role="scroll"]')!;
  const rowsContainer = el.querySelector<HTMLDivElement>('[data-role="rows"]')!;

  const rowStates = new Map<number, RowState>();
  let raf: number | null = null;
  let isEmpty = true;

  function buildRow(r: LbRow): RowState {
    const rowEl = document.createElement('div');
    rowEl.className = `sg-lblb-row ${medalFor(r.rank)}`.trim();
    rowEl.innerHTML = `
      <div class="sg-lblb-rank">${r.rank}</div>
      <div class="sg-lblb-name" data-role="name"></div>
      <div class="sg-lblb-points" data-role="score">${r.total_points.toLocaleString()}</div>
    `;
    const rankEl = rowEl.querySelector<HTMLDivElement>('.sg-lblb-rank')!;
    const nameEl = rowEl.querySelector<HTMLDivElement>('[data-role="name"]')!;
    const scoreEl = rowEl.querySelector<HTMLDivElement>('[data-role="score"]')!;
    nameEl.textContent = r.display_name;
    return {
      playerId: r.player_id,
      displayedScore: r.total_points,
      targetScore: r.total_points,
      animFrom: r.total_points,
      animStartMs: null,
      rowEl,
      nameEl,
      scoreEl,
      rankEl,
    };
  }

  function recomputeTravel(): void {
    const contentH = scroll.scrollHeight;
    const containerH = body.clientHeight;
    const overflow = Math.max(0, contentH - containerH);
    scroll.style.setProperty('--sg-lblb-travel', `${-overflow}px`);
    const dur = Math.max(30, Math.round(overflow / 60));
    scroll.style.setProperty('--sg-lblb-duration', `${dur}s`);
  }

  function setRows(rows: LbRow[]): void {
    if (rows.length === 0) {
      if (!isEmpty) {
        rowsContainer.innerHTML = '<div class="sg-lblb-empty">No scores yet — be the first to answer!</div>';
        for (const s of rowStates.values()) s.rowEl.remove();
        rowStates.clear();
        isEmpty = true;
        requestAnimationFrame(recomputeTravel);
      }
      return;
    }
    if (isEmpty) {
      rowsContainer.innerHTML = '';
      isEmpty = false;
    }
    // Rebuild the ordered list each call so ties/swaps reorder without flicker.
    // Reuses existing row nodes via the rowStates map so score-count animations
    // don't restart on every snapshot.
    const seen = new Set<number>();
    const fragment = document.createDocumentFragment();
    for (const r of rows) {
      seen.add(r.player_id);
      let state = rowStates.get(r.player_id);
      if (!state) {
        state = buildRow(r);
        rowStates.set(r.player_id, state);
      } else {
        const desiredCls = `sg-lblb-row ${medalFor(r.rank)}`.trim();
        if (state.rowEl.className !== desiredCls) state.rowEl.className = desiredCls;
        if (state.rankEl.textContent !== String(r.rank)) state.rankEl.textContent = String(r.rank);
        if (state.nameEl.textContent !== r.display_name) state.nameEl.textContent = r.display_name;
      }
      fragment.appendChild(state.rowEl);
      if (state.targetScore !== r.total_points) {
        state.animFrom = state.displayedScore;
        state.targetScore = r.total_points;
        state.animStartMs = performance.now();
      }
      const nameEl = state.nameEl;
      requestAnimationFrame(() => fitText(nameEl, { min: 10, max: 17 }));
    }
    rowsContainer.replaceChildren(fragment);
    // Drop states no longer present.
    for (const [pid, s] of rowStates) {
      if (!seen.has(pid)) {
        s.rowEl.remove();
        rowStates.delete(pid);
      }
    }
    scheduleTick();
    requestAnimationFrame(recomputeTravel);
  }

  function scheduleTick(): void {
    if (raf !== null) return;
    raf = requestAnimationFrame(tick);
  }

  function tick(): void {
    raf = null;
    let anyAnimating = false;
    const now = performance.now();
    for (const s of rowStates.values()) {
      if (s.animStartMs === null) continue;
      const t = Math.min(1, (now - s.animStartMs) / 600);
      const e = 1 - Math.pow(1 - t, 3);
      const val = Math.round(s.animFrom + (s.targetScore - s.animFrom) * e);
      if (val !== s.displayedScore) {
        s.displayedScore = val;
        s.scoreEl.textContent = val.toLocaleString();
      }
      if (t >= 1) {
        s.displayedScore = s.targetScore;
        s.scoreEl.textContent = s.targetScore.toLocaleString();
        s.animStartMs = null;
      } else {
        anyAnimating = true;
      }
    }
    if (anyAnimating) scheduleTick();
  }

  function setVisible(v: boolean): void {
    el.classList.toggle('hidden', !v);
    if (v) requestAnimationFrame(recomputeTravel);
  }

  return {
    el,
    setRows,
    setVisible,
    dispose() {
      if (raf !== null) cancelAnimationFrame(raf);
      el.remove();
    },
  };
}
